import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { cloneDeep, pick } from 'lodash'
import { httpsCallable } from 'firebase/functions'
import { useBackendStore } from './backend'
import { useMainStore } from './main'
import { useContactStore } from './contact'

export const useMapSharingStore = defineStore('mapSharing', () => {
  // State
  const mapId = ref(null)
  const mapScreenshotUrl = ref(null)

  // Getters
  const sharingLink = computed(() => {
    const baseUrl = import.meta.env.VITE_BASE_URL || `${window.location.origin}`
    return mapId.value ? `${baseUrl}/map/${mapId.value}/results` : ''
  })

  // Actions
  function resetState() {
    mapId.value = null
  }

  async function saveSharedMap({ data }) {
    const backendStore = useBackendStore()
    const fn = httpsCallable(backendStore.functions, 'saveSharedMap')
    const result = await fn(data)

    return result.data
  }

  async function prepareAndSaveMap({ data = null } = {}) {
    const mainStore = useMainStore()
    const contactStore = useContactStore()

    mainStore.setLoading(true, 'Saving measurements...')
    try {
      const stateData = data || {
        mode: mainStore.mode,
        address: mainStore.address,
        displayAddress: mainStore.displayAddress,
        shapes: mainStore.getShapesArray(),
        globalZIndex: mainStore.globalZIndex,
        mapZoom: mainStore.mapZoom,
        mapCenter: mainStore.mapCenter,
        projectName: mainStore.projectName
      }

      let copiedState = cloneDeep(pick(stateData, [
        'mode',
        'address',
        'displayAddress',
        'shapes',
        'globalZIndex',
        'mapZoom',
        'mapCenter',
        'projectName'
      ]))

      delete copiedState.mapCenter['fromMap']
      delete copiedState.mapZoom['fromMap']

      // Copy the convertedCalculated and suffix properties from the shapes getter
      copiedState.shapes = copiedState.shapes.map(shape => {
        let shapeGetter = mainStore.shapesWithConversions.find(s => s.indexID === shape.indexID)
        return {
          ...shape,
          convertedCalculated: shapeGetter ? shapeGetter.convertedCalculated : shape.convertedCalculated,
          suffix: shapeGetter ? shapeGetter.suffix : shape.suffix
        }
      })

      copiedState.totalShapeCalculatedConverted = mainStore.totalShapeCalculatedConverted
      copiedState.totalShapeCalculatedRaw = mainStore.totalShapeCalculatedRaw

      copiedState.meta = {
        mapId: mapId.value,
        saved: Date.now(),
        utm: mainStore.utm,
        schemaVersion: (data) ? data.meta.schemaVersion : import.meta.env.VITE_SAVE_SCHEMA_VERSION,
        sessionUuid: mainStore.uuid,
        customerFirstName: contactStore.firstName,
        customerLastName: contactStore.lastName,
        customerEmail: contactStore.email,
        customerPhone: contactStore.phone,
        customerProductInterest: contactStore.productInterest,
        customerAgree: contactStore.agree
      }

      return await saveSharedMap({ data: copiedState })
    } catch (e) {
      throw new Error(e)
    } finally {
      mainStore.setLoading(false)
    }
  }

  async function shareMap(payload) {
    const mainStore = useMainStore()
    const result = await prepareAndSaveMap(payload)
    const { mapId: resultMapId, mapScreenshotUrl: resultUrl, meta } = result

    mapId.value = resultMapId
    mapScreenshotUrl.value = resultUrl
    mainStore.setMeta(meta)

    return result
  }

  async function updateSharedMap(payload) {
    const mainStore = useMainStore()
    const { mapId: resultMapId, mapScreenshotUrl: resultUrl, meta } = await prepareAndSaveMap(payload)

    mapId.value = resultMapId
    mapScreenshotUrl.value = resultUrl
    mainStore.setMeta(meta)
  }

  async function getSharedMap({ mapId: mapIdParam }) {
    const backendStore = useBackendStore()
    const mainStore = useMainStore()

    mainStore.setLoading(true, 'Loading map...')
    try {
      const loadMap = httpsCallable(backendStore.functions, 'getSharedMap')
      const result = await loadMap({ mapId: mapIdParam })

      if (!result.data) {
        throw new Error('Map not found')
      }

      result.data.mapCenter = { ...result.data.mapCenter, fromSave: true }

      return { data: result.data, mapId: mapIdParam }
    } catch (e) {
      throw new Error(e)
    } finally {
      mainStore.setLoading(false)
    }
  }

  return {
    // State
    mapId,
    mapScreenshotUrl,

    // Getters
    sharingLink,

    // Actions
    resetState,
    saveSharedMap,
    prepareAndSaveMap,
    shareMap,
    updateSharedMap,
    getSharedMap
  }
})
